import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import type { RouterContext } from '@shared/config/router-context';
import { AuthLoading, useAuth } from '@features/auth';
import { StudentDashboard } from './_authenticated/student/index';

/**
 * Student Dashboard Route (Alias)
 *
 * Route: /:locale/student
 * Mirrors the authenticated student dashboard path.
 */
export const Route = createFileRoute('/$locale/student')({
  beforeLoad: ({ context, location, params }) => {
    const { auth } = context as RouterContext;

    if (location.pathname === `/${params.locale}/login`) {
      return;
    }

    if (auth.isLoading) {
      return;
    }

    if (!auth.isAuthenticated) {
      return redirect({
        to: '/$locale/login',
        params: { locale: params.locale },
        search: { redirect: location.pathname },
      });
    }
  },
  component: StudentRoute,
});

function StudentRoute() {
  const { locale } = Route.useParams();
  const navigate = useNavigate();
  const { isLoading, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) return;
    void navigate({
      to: '/$locale/login',
      params: { locale },
      search: { redirect: `/${locale}/student` },
      replace: true,
    });
  }, [isAuthenticated, isLoading, locale, navigate]);

  if (isLoading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    return <AuthLoading message="Redirecting to login..." />;
  }

  return <StudentDashboard />;
}
